import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Package, Shield, LogOut, Heart } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useShopSettings } from "@/context/ShopSettingsContext";
import { PrelaunchBanner } from "./PrelaunchBanner";

const UserMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-button" className="btn-ghost-mc !px-2 !py-1.5">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />
          ) : (
            <User className="h-5 w-5" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profilo")}>
          <User className="mr-2 h-4 w-4" /> Profilo
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-my-orders" onClick={() => navigate("/ordini")}>
          <Package className="mr-2 h-4 w-4" /> I miei ordini
        </DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
            <Shield className="mr-2 h-4 w-4" /> Admin negozio
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
          <LogOut className="mr-2 h-4 w-4" /> Esci
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const WishlistLink = () => {
  const { count } = useWishlist();
  return (
    <Link to="/preferiti" data-testid="wishlist-header-button" aria-label="Preferiti" className="btn-ghost-mc relative !px-2.5 !py-2">
      <Heart className="h-5 w-5" />
      {count > 0 && (
        <span data-testid="wishlist-count-badge" className="absolute -right-2 -top-2 grid h-6 min-w-6 place-items-center rounded-full border-2 border-[#0b0b0f] bg-[#ff2e63] px-1 text-xs font-black text-white">
          {count}
        </span>
      )}
    </Link>
  );
};

export const Header = () => {
  const { user, loading } = useAuth();
  const { count } = useCart();
  const { showcase_mode } = useShopSettings();
  return (
    <>
    <PrelaunchBanner />
    <header className="sticky top-0 z-40 bg-[#0b0b0f]">
      <div className="mx-auto grid h-24 max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-2 sm:h-32 sm:px-6 lg:px-8">
        <div className="flex items-center">
          {user?.is_admin && (
            <Link to="/admin" data-testid="header-admin-link" className="btn-ghost-mc !px-2.5 !py-1.5">
              <Shield className="h-4 w-4" /> <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
        </div>
        <Link to="/" data-testid="header-home-link" className="flex h-full items-center justify-center py-1">
          <Logo className="h-[84px] sm:h-[120px]" />
        </Link>
        <nav className="flex items-center justify-end gap-2">
          <WishlistLink />
          {!showcase_mode && <Link to="/carrello" data-testid="cart-header-button" className="btn-mc relative !px-3 !py-2">
            <ShoppingBag className="h-5 w-5" />
            <span className="hidden sm:inline">Carrello</span>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-2 -top-2 grid h-6 min-w-6 place-items-center rounded-full border-2 border-[#0b0b0f] bg-white px-1 text-xs font-black"
              >
                {count}
              </span>
            )}
          </Link>}
          {!loading && (user ? <UserMenu /> : (
            <Link to="/login" data-testid="header-login-button" className="btn-ghost-mc !py-2">
              <User className="h-4 w-4" /> <span className="hidden sm:inline">Accedi</span>
            </Link>
          ))}
        </nav>
      </div>
      <div className="neon-line" />
    </header>
    </>
  );
};
